import { useState, memo, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTranslation } from 'react-i18next';

// Revenue Department designations (full list for create official)
const REVENUE_DESIGNATIONS = [
  { value: "tahsildar", labelKey: "designation.tahsildar" },
  { value: "deputy_tahsildar", labelKey: "designation.deputy_tahsildar" },
  { value: "shirestedar", labelKey: "designation.shirestedar" },
  { value: "revenue_inspector", labelKey: "designation.revenue_inspector" },
  { value: "village_accountant", labelKey: "designation.village_accountant" },
  { value: "first_division_clerk", labelKey: "designation.first_division_clerk" },
  { value: "second_division_clerk", labelKey: "designation.second_division_clerk" },
];

// Survey Department designations (full list for create official)
const SURVEY_DESIGNATIONS = [
  { value: "ddlr", labelKey: "designation.ddlr" },
  { value: "adlr", labelKey: "designation.adlr" },
  { value: "surveyor", labelKey: "designation.surveyor" },
];

const DEPARTMENTS = [
  { value: "revenue", labelKey: "department.revenue" },
  { value: "survey", labelKey: "department.survey" },
];

const CreateOfficialDialog = memo(function CreateOfficialDialog({ 
  open, 
  onOpenChange, 
  userDepartment,
  onSubmit 
}) {
  const { t } = useTranslation();
  
  // Use refs for text inputs (uncontrolled) to avoid re-renders on each keystroke
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const passwordRef = useRef(null);
  
  // Only use state for select components (they need controlled behavior)
  const [designation, setDesignation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Clear all input refs
      if (nameRef.current) nameRef.current.value = "";
      if (emailRef.current) emailRef.current.value = "";
      if (phoneRef.current) phoneRef.current.value = "";
      if (passwordRef.current) passwordRef.current.value = "";
      setDesignation("");
    }
  }, [open]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = {
      name: nameRef.current?.value || "",
      email: emailRef.current?.value || "",
      phone: phoneRef.current?.value || "",
      password: passwordRef.current?.value || "",
      department: userDepartment || "",
      designation: designation
    };
    
    try {
      await onSubmit(formData);
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in parent
    } finally {
      setIsSubmitting(false);
    }
  }, [userDepartment, designation, onSubmit, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('officialsManagement.createOfficial')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          {/* Hidden fields to confuse browser autofill */}
          <input type="text" name="fakeusernameremembered" style={{display: 'none'}} tabIndex={-1} />
          <input type="password" name="fakepasswordremembered" style={{display: 'none'}} tabIndex={-1} />
          
          <div>
            <Label>{t('officialsManagement.name')}</Label>
            <Input
              ref={nameRef}
              required
              className="mt-1"
              data-testid="new-official-name"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              data-lpignore="true"
            />
          </div>
          <div>
            <Label>{t('officialsManagement.email')}</Label>
            <Input
              ref={emailRef}
              type="text"
              inputMode="email"
              required
              className="mt-1"
              data-testid="new-official-email"
              autoComplete="off"
              data-form-type="other"
              data-lpignore="true"
              placeholder="example@email.com"
            />
          </div>
          <div>
            <Label>{t('officialsManagement.phone')}</Label>
            <Input
              ref={phoneRef}
              type="text"
              inputMode="tel"
              required
              className="mt-1"
              data-testid="new-official-phone"
              autoComplete="off"
              data-form-type="other"
              data-lpignore="true"
              placeholder="9876543210"
            />
          </div>
          <div>
            <Label>{t('officialsManagement.password')}</Label>
            <Input
              ref={passwordRef}
              type="password"
              required
              className="mt-1"
              data-testid="new-official-password"
              autoComplete="new-password"
              data-form-type="other"
              data-lpignore="true"
            />
          </div>
          <div>
            <Label>{t('officialsManagement.department')}</Label>
            <Select 
              value={userDepartment || ""} 
              disabled={true}
            >
              <SelectTrigger className="mt-1 bg-stone-100" data-testid="new-official-department">
                <SelectValue placeholder={t('department.selectDepartment')} />
              </SelectTrigger>
              <SelectContent>
                {DEPARTMENTS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {t(d.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">{t('officialsManagement.departmentLockedToYours')}</p>
          </div>
          <div>
            <Label>{t('officialsManagement.designation')}</Label>
            <Select 
              value={designation} 
              onValueChange={setDesignation}
              disabled={!userDepartment}
            >
              <SelectTrigger className="mt-1" data-testid="new-official-designation">
                <SelectValue placeholder={userDepartment ? t('designation.selectDesignation') : t('designation.selectDepartmentFirst')} />
              </SelectTrigger>
              <SelectContent>
                {userDepartment === "revenue" && REVENUE_DESIGNATIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {t(d.labelKey)}
                  </SelectItem>
                ))}
                {userDepartment === "survey" && SURVEY_DESIGNATIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {t(d.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('officialsManagement.cancel')}
            </Button>
            <Button 
              type="submit" 
              className="bg-emerald-800 hover:bg-emerald-900 text-white" 
              data-testid="submit-create-official"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.processing') : t('officialsManagement.create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
});

export default CreateOfficialDialog;
